import { type RouteCallback, useGetRoute, useMatchPath } from '@faceup/router'
import { getPathnameOnly } from '@faceup/utils'
import type { AdminMenuItem, AdminMenuSection } from './AdminMenu'
import { isNavigable, isSectionVisible, isVisible } from './adminMenuUtils'

type ResolvePath = (path: RouteCallback) => string
type MatchPattern = (pattern: string) => { pattern: { path: string } } | null

type MenuLeaf = {
  key: string
  sectionKey?: string
  paths: (RouteCallback | string)[]
}

const collectSectionLeaves = (sections: AdminMenuSection[], sectionKey: string): MenuLeaf[] =>
  sections
    .filter(isSectionVisible)
    .flatMap(section => section.items)
    .filter(isNavigable)
    .map(item => ({ key: item.key, sectionKey, paths: [item.link, ...(item.match ?? [])] }))

export const collectLeaves = (items: AdminMenuItem[]): MenuLeaf[] =>
  items.filter(isVisible).flatMap(item => {
    if (item.link) {
      return [{ key: item.key, paths: [item.link, ...(item.match ?? [])] }]
    }

    if (item.secondary) {
      return collectSectionLeaves(
        [...item.secondary.sections, ...(item.secondary.footerSections ?? [])],
        item.key
      )
    }

    return []
  })

export const findSectionKey = (items: AdminMenuItem[], activeKey: string) =>
  collectLeaves(items).find(leaf => leaf.key === activeKey)?.sectionKey

export const pickActiveLeaf = (
  items: AdminMenuItem[],
  resolvePath: ResolvePath,
  matchPattern: MatchPattern
) => {
  const scoreLeaf = (leaf: MenuLeaf) =>
    leaf.paths.reduce((best, path) => {
      const pattern = typeof path === 'string' ? path : getPathnameOnly(resolvePath(path))
      const match = matchPattern(pattern)
      return match ? Math.max(best, match.pattern.path.split('/').length) : best
    }, 0)

  const winner = collectLeaves(items).reduce<{ leaf?: MenuLeaf; score: number }>(
    (best, leaf) => {
      const score = scoreLeaf(leaf)
      return score > best.score ? { leaf, score } : best
    },
    { score: 0 }
  )

  return { activeKey: winner.leaf?.key, sectionKey: winner.leaf?.sectionKey }
}

export const useActiveMenuKey = (items: AdminMenuItem[]) => {
  const getRoute = useGetRoute()
  const matchPath = useMatchPath()

  return pickActiveLeaf(items, getRoute, pattern => matchPath(pattern, { wildcard: true }))
}
